import React from 'react';
import { confirmAlert } from 'components/common';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { FaPlus, FaEdit, FaTrashAlt, FaListUl, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { deleteSalesUnit } from 'redux/crm/salesUnitSlice';

const Row = ({ label, value, isBadge }) => (
  <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-start tw-py-2 tw-border-b tw-border-slate-100 last:tw-border-0">
    <span className="sm:tw-w-36 sm:tw-shrink-0 tw-text-xs tw-font-semibold tw-text-slate-500 tw-uppercase tw-tracking-wide tw-pt-0.5">
      {label}
    </span>
    {isBadge ? (
      value ? (
        <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-px-2 tw-py-0.5 tw-rounded-xl tw-bg-emerald-50 tw-text-emerald-700">
          <FaCheckCircle size={10} /> Yes
        </span>
      ) : (
        <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-px-2 tw-py-0.5 tw-rounded-xl tw-bg-slate-100 tw-text-slate-500">
          No
        </span>
      )
    ) : (
      <span className="tw-text-sm tw-text-slate-800">{value || <span className="tw-text-slate-300">—</span>}</span>
    )}
  </div>
);

Row.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.node, isBadge: PropTypes.bool };

const actionBtn =
  'tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded tw-text-sm tw-font-medium tw-cursor-pointer tw-transition-colors';

const SalesUnitDetailHeader = ({ sales_unit, onNew, onEdit }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  if (!sales_unit) return null;

  const backToList = () => navigate('/crm/sales-units/list');

  const handleDelete = () => {
    confirmAlert({
      title: 'Delete Sales Unit',
      message: `Delete sales unit <strong>${sales_unit.name}</strong>? This cannot be undone.`,
      type: 'danger',
      onConfirm: async () => {
        try {
          await dispatch(deleteSalesUnit(sales_unit.id)).unwrap();
          toast.success(`Sales unit "${sales_unit.name}" was deleted.`);
          backToList();
        } catch (err) {
          toast.error(err?.error || err?.message || 'Failed to delete sales unit.');
        }
      },
    });
  };

  return (
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm tw-mb-3">
      {/* Header bar */}
      <div className="tw-flex tw-items-start tw-justify-between tw-flex-wrap tw-gap-3 tw-px-5 tw-py-4 tw-border-b tw-border-slate-100">
        <div>
          <div className="tw-flex tw-items-center tw-gap-2.5">
            <h5 className="tw-text-lg tw-font-bold tw-text-slate-800 tw-mb-0">{sales_unit.name}</h5>
            {sales_unit.active ? (
              <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-px-2 tw-py-0.5 tw-rounded-xl tw-bg-emerald-50 tw-text-emerald-700">
                <FaCheckCircle size={10} /> Activated
              </span>
            ) : (
              <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-px-2 tw-py-0.5 tw-rounded-xl tw-bg-rose-50 tw-text-rose-700">
                <FaTimesCircle size={10} /> Not active
              </span>
            )}
          </div>
          <p className="tw-text-xs tw-text-slate-400 tw-mt-0.5 tw-mb-0">Sales Unit ID#: {sales_unit.id}</p>
        </div>

        <div className="tw-flex tw-items-center tw-gap-2">
          <button type="button" onClick={onNew}
            className={`${actionBtn} tw-border tw-border-slate-300 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50`}>
            <FaPlus size={11} /> New
          </button>
          <button type="button" onClick={onEdit}
            className={`${actionBtn} tw-border-0 tw-bg-primary-600 tw-text-white hover:tw-bg-primary-700`}>
            <FaEdit size={12} /> Edit
          </button>
          <button type="button" onClick={handleDelete}
            className={`${actionBtn} tw-border tw-border-rose-300 tw-bg-rose-50 tw-text-rose-700 hover:tw-bg-rose-100`}>
            <FaTrashAlt size={11} /> Delete
          </button>
          <button type="button" onClick={backToList}
            className={`${actionBtn} tw-border tw-border-slate-300 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50`}>
            <FaListUl size={12} /> Back to list
          </button>
        </div>
      </div>

      {/* Info grid */}
      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-x-10 tw-px-5 tw-py-3">
        <div>
          <Row label="Description" value={sales_unit.description} />
          <Row label="Parent" value={sales_unit.parent_id} />
          <Row label="Region" value={sales_unit.region} />
          <Row label="Site" value={sales_unit.site_name} />
        </div>
        <div>
          <Row label="Type of Sales" value={sales_unit.type_of_sales} />
          <Row label="Sales office" value={sales_unit.is_sales_office} isBadge />
          <Row label="Created by" value={sales_unit.created_by} />
          <Row label="Created date" value={sales_unit.created_at} />
        </div>
      </div>
    </div>
  );
};

SalesUnitDetailHeader.propTypes = {
  sales_unit: PropTypes.shape({
    id: PropTypes.number,
    active: PropTypes.bool,
    name: PropTypes.string,
    description: PropTypes.string,
    parent_id: PropTypes.string,
    region: PropTypes.string,
    site_name: PropTypes.string,
    type_of_sales: PropTypes.string,
    is_sales_office: PropTypes.bool,
    created_by: PropTypes.string,
    created_at: PropTypes.string,
  }),
  onNew: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
};

export default SalesUnitDetailHeader;
